import { t } from '../i18n/nl.js';

// ════════════════════════════════════════════════════════════════
// Hulpfuncties voor de coachpagina "Push notificaties" (/coach/push).
// Bewust los van de React-component zodat ze zonder DOM getest kunnen
// worden (scripts/test-push-broadcast.js).
// ════════════════════════════════════════════════════════════════

// Expo staat 4096 bytes payload per bericht toe (docs.expo.dev,
// push-notifications/sending-notifications). De praktische grens ligt véél
// lager: Android/iOS kappen een notificatietitel al na ongeveer één regel af
// en tonen de tekst ingeklapt op zo'n twee regels. Daarom een ruime
// harde grens plus een waarschuwing zodra afkappen waarschijnlijk wordt.
// Deze twee maxima staan óók in supabase/functions/send-broadcast-push —
// bij wijziging beide bijwerken.
export const TITEL_MAX = 100;
export const TEKST_MAX = 500;
export const TITEL_WAARSCHUWING = 40;
export const TEKST_WAARSCHUWING = 150;

/** Tekens tellen zoals een gebruiker ze ziet (emoji = 1). */
export function tekenAantal(waarde) {
  return [...(waarde || '')].length;
}

/**
 * Validatie van titel en berichttekst.
 * `fout` blokkeert verzenden, `waarschuwing` is informatief.
 */
export function valideerBericht(titel, tekst) {
  const titelLengte = tekenAantal(titel.trim());
  const tekstLengte = tekenAantal(tekst.trim());

  return {
    titelLengte,
    tekstLengte,
    titelFout: titelLengte === 0
      ? 'leeg'
      : titelLengte > TITEL_MAX ? 'te_lang' : null,
    tekstFout: tekstLengte === 0
      ? 'leeg'
      : tekstLengte > TEKST_MAX ? 'te_lang' : null,
    titelWaarschuwing: titelLengte > TITEL_WAARSCHUWING && titelLengte <= TITEL_MAX,
    tekstWaarschuwing: tekstLengte > TEKST_WAARSCHUWING && tekstLengte <= TEKST_MAX,
    geldig:
      titelLengte > 0 && titelLengte <= TITEL_MAX &&
      tekstLengte > 0 && tekstLengte <= TEKST_MAX,
  };
}

/**
 * Vertaalt een stabiele foutcode van de Edge Function naar begrijpelijke
 * Nederlandse tekst. Ruwe Supabase-/Expo-/EF-fouttekst komt nooit bij de
 * gebruiker terecht — die gaat naar de console/Sentry.
 */
export function broadcastFoutTekst(code, extra = {}) {
  switch (code) {
    case 'geen_rechten':
      return t('broadcastPush.foutGeenRechten');
    case 'sessie_verlopen':
      return t('broadcastPush.foutSessieVerlopen');
    case 'lege_inhoud':
      return t('broadcastPush.foutLegeInhoud');
    case 'titel_te_lang':
      return t('broadcastPush.foutTitelTeLang').replace('{max}', extra.max ?? TITEL_MAX);
    case 'tekst_te_lang':
      return t('broadcastPush.foutTekstTeLang').replace('{max}', extra.max ?? TEKST_MAX);
    case 'daglimiet_bereikt':
      return t('broadcastPush.rateLimitBereikt')
        .replace('{used}', extra.used ?? '?')
        .replace('{limit}', extra.limit ?? '?');
    case 'geen_ontvangers':
      return t('broadcastPush.foutGeenOntvangers');
    case 'verzending_nog_bezig':
      return t('broadcastPush.foutVerzendingNogBezig');
    case 'vorige_verzending_mislukt':
      return t('broadcastPush.foutVorigeVerzendingMislukt');
    case 'ongeldige_invoer':
      return t('broadcastPush.foutOngeldigeInvoer');
    case 'tokens_ophalen_mislukt':
    case 'claim_mislukt':
    case 'onbekende_fout':
    default:
      return t('broadcastPush.foutVersturenAlgemeen');
  }
}

/** Datum + tijd in de Amsterdamse tijdzone, zoals elders in de portal. */
export function formatMoment(isoString) {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString('nl-NL', {
    timeZone: 'Europe/Amsterdam',
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/** Willekeurige idempotency-key; beschermt tegen dubbelklikken en retries. */
export function maakIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Roept de Edge Function send-broadcast-push aan en normaliseert het
 * antwoord. Bij een non-2xx is `res.data` leeg en staat de body in
 * `res.error.context` — zonder die uit te lezen zie je alleen een
 * generieke "non-2xx status"-melding.
 */
export async function verstuurBroadcast(supabase, payload) {
  async function invoke(session) {
    return supabase.functions.invoke('send-broadcast-push', {
      body: payload,
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
  }

  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  let session = sessionData?.session;
  if (sessionError || !session?.access_token) {
    return { ok: false, error: 'sessie_verlopen' };
  }

  let res = await invoke(session);

  // Een browser kan vlak rond tokenvernieuwing nog één request met de oude
  // access-token starten. Vernieuw bij een gateway-401 precies één keer en
  // herhaal met de nieuwe token; andere fouten worden nooit automatisch
  // opnieuw verstuurd.
  if (res.error?.context?.status === 401) {
    const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
    session = refreshData?.session;
    if (!refreshError && session?.access_token) {
      res = await invoke(session);
    } else {
      // De refresh-token is ingetrokken of ongeldig. Ruim alleen de lokale
      // sessie op; de gebruiker moet opnieuw inloggen om nieuwe tokens te
      // krijgen.
      await supabase.auth.signOut({ scope: 'local' });
      return { ok: false, error: 'sessie_verlopen' };
    }
  }

  if (res.error) {
    let body = null;
    try {
      body = await res.error.context?.json?.();
    } catch {
      body = null;
    }
    if (body && typeof body === 'object') return { ...body, ok: body.ok === true };
    console.error('send-broadcast-push: onbekende fout', res.error);
    return { ok: false, error: 'onbekende_fout' };
  }

  return res.data ?? { ok: false, error: 'onbekende_fout' };
}

/**
 * Bouwt de resultaatmelding na verzending. Elke oorzaak apart — een
 * mislukte verzending heet nooit "zonder pushtoken" als de werkelijke
 * oorzaak anders of onbekend is.
 */
export function resultaatRegels(counts) {
  const regels = [];
  regels.push(t('broadcastPush.resultaatGeaccepteerd').replace('{count}', counts.accepted ?? 0));
  if (counts.noToken > 0) {
    regels.push(t('broadcastPush.resultaatGeenToken').replace('{count}', counts.noToken));
  }
  if (counts.optOut > 0) {
    regels.push(t('broadcastPush.resultaatOptOut').replace('{count}', counts.optOut));
  }
  if (counts.rejected > 0) {
    regels.push(t('broadcastPush.resultaatGeweigerd').replace('{count}', counts.rejected));
  }
  if (counts.error > 0) {
    regels.push(t('broadcastPush.resultaatFout').replace('{count}', counts.error));
  }
  return regels;
}

/** Samenvatting van de actieve filters, voor de bevestiging en geschiedenis. */
export function filterSamenvatting(filters, locaties, abonnementen, coaches) {
  const naam = (lijst, id, veld) => lijst.find(x => x.id === id)?.[veld] || id;
  const delen = [];
  if (filters.locationId) {
    delen.push(`${t('broadcastPush.filterLocatieKort')}: ${naam(locaties, filters.locationId, 'name')}`);
  }
  if (filters.membershipTypeId) {
    delen.push(`${t('broadcastPush.filterAbonnementstypeKort')}: ${naam(abonnementen, filters.membershipTypeId, 'name')}`);
  }
  if (filters.coachId) {
    delen.push(`${t('broadcastPush.filterCoachKort')}: ${naam(coaches, filters.coachId, 'full_name')}`);
  }
  return delen.length > 0 ? delen.join(' · ') : t('broadcastPush.filterGeen');
}
